import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterOutlet,
  RouterModule
} from '@angular/router';
import { delay, filter, map, tap } from 'rxjs/operators';
import { ColorModeService } from '@coreui/angular';
import { IconSetService } from '@coreui/icons-angular';
import { iconSubset } from './icons/icon-subset';
import { ReactiveFormsModule } from '@angular/forms';
import { ThemeService, AppTheme, DEFAULT_APP_THEME } from './service/theme.service';
import { UserService } from './service/user.service';
import { UserPreferencesService } from './service/user-preferences.service';
import { LocaleService, normalizeAppLocale } from './service/locale.service';
import { SeoService } from './service/seo.service';
import { TokenStorageService } from '@services/token-storage.service';


@Component({
  selector: 'app-root',
  template: '<router-outlet />',
  standalone: true,
  imports: [RouterOutlet, ReactiveFormsModule, RouterModule]
})
export class AppComponent implements OnInit {
  title = 'IMA One';

  readonly #destroyRef: DestroyRef = inject(DestroyRef);
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #titleService = inject(Title);

  readonly #colorModeService = inject(ColorModeService);
  readonly #iconSetService = inject(IconSetService);
  readonly #themeService = inject(ThemeService);
  readonly #userService = inject(UserService);
  readonly #userPreferencesService = inject(UserPreferencesService);
  readonly #localeService = inject(LocaleService);
  readonly #seoService = inject(SeoService);

  constructor() {
    this.#titleService.setTitle(this.title);
    this.#iconSetService.icons = { ...iconSubset };
    this.#colorModeService.localStorageItemName.set('ima_ai_web');
    this.#colorModeService.eventName.set('ColorSchemeChange');
    this.#themeService.init();
  }

  private routeTreeHasPreLoginUnifiedTheme(snap: ActivatedRouteSnapshot | null): boolean {
    let p: ActivatedRouteSnapshot | null = snap;
    while (p) {
      if (p.data['preLoginUnifiedTheme'] === true) {
        return true;
      }
      p = p.firstChild;
    }
    return false;
  }

  private syncBodyThemeToRoute(): void {
    const preLogin = this.routeTreeHasPreLoginUnifiedTheme(this.#router.routerState.snapshot.root);
    // 已登入者在任何頁面（含論壇/行銷頁）都跟隨個人偏好主題；
    // 未登入訪客在行銷/公開頁採用統一行銷主題，其餘採系統預設。
    if (TokenStorageService.getToken()) {
      this.#themeService.restorePreferredThemeFromStorage();
      return;
    }
    if (preLogin) {
      this.#themeService.applyPreLoginMarketingTheme();
    } else {
      this.#themeService.setTheme(DEFAULT_APP_THEME);
    }
  }

  ngOnInit(): void {
    queueMicrotask(() => this.syncBodyThemeToRoute());

    this.#userService.get_self_info().pipe(
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe((data) => {
    this.#userPreferencesService.applyFromUser(data?.user);
    const apiLocaleRaw = data?.user?.user_preferences?.locale;
    const hasStoredLocale = !!localStorage.getItem('ima_locale');
    if (this.#localeService.isManualSelection || hasStoredLocale) {
      void this.#localeService.reapplyStored();
    } else if (apiLocaleRaw) {
      this.#localeService.use(normalizeAppLocale(apiLocaleRaw), true, false);
    }
    this.#themeService.syncProfilePreferenceFromApi(data?.user?.theme_preference);
      this.syncBodyThemeToRoute();
    });

    this.#router.events.pipe(
        takeUntilDestroyed(this.#destroyRef),
        filter((evt): evt is NavigationEnd => evt instanceof NavigationEnd)
      ).subscribe(() => {
        this.syncBodyThemeToRoute();
        this.#seoService.syncForCurrentRoute();
      });

    this.#localeService.currentLocale$.pipe(
      takeUntilDestroyed(this.#destroyRef),
    ).subscribe(() => this.#seoService.syncForCurrentRoute());

    this.#activatedRoute.queryParams
      .pipe(
        delay(1),
        map((params: Record<string, unknown>) => {
          const raw = params['theme'];
          if (typeof raw !== 'string') {
            return undefined;
          }
          return raw.match(/^[A-Za-z0-9\s]+/)?.[0];
        }),
        filter((t): t is 'dark' | 'light' | 'auto' => t === 'dark' || t === 'light' || t === 'auto'),
        tap((theme) => {
          if (!this.routeTreeHasPreLoginUnifiedTheme(this.#router.routerState.snapshot.root)) {
            this.#colorModeService.colorMode.set(theme);
          }
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();

    this.#seoService.syncForCurrentRoute();
  }
}
